import React from "react";
import { toast } from "sonner";
import {
  Loader2,
  MapPin,
  Play,
  RefreshCw,
  Search,
  Warehouse,
  CheckCircle2,
  Circle,
  Truck,
  Store,
  Shield,
  UserCheck,
} from "lucide-react";
import { adminApi } from "../../lib/api";
import DistributionLinehaulPanel from "./DistributionLinehaulPanel";
import DistributionPakkeshopPanel from "./DistributionPakkeshopPanel";
import DistributionDriftPanel from "./DistributionDriftPanel";
import DistributionSubcontractorPanel from "./DistributionSubcontractorPanel";

const REGION_LABELS = {
  zealand: "Sjælland",
  bornholm: "Bornholm",
  funen: "Fyn",
  jutland: "Jylland",
};

const PRODUCT_OPTIONS = [
  { value: "same_day_evening", label: "Same Day aften" },
  { value: "next_day", label: "Next Day" },
];

const MODE_OPTIONS = [
  { value: "full", label: "Pickup + levering" },
  { value: "pickup", label: "Kun pickup" },
  { value: "delivery", label: "Kun levering" },
];

const SETTLEMENT_OPTIONS = [
  { value: "internal_driver", label: "EMA chauffør" },
  { value: "subcontractor", label: "Underleverandør (vognmand)" },
];

const MAIN_TABS = [
  { k: "hubs", label: "HUBs & plan", icon: Warehouse },
  { k: "linehaul", label: "Linehaul", icon: Truck },
  { k: "vognmand", label: "Vognmand", icon: UserCheck },
  { k: "pakkeshop", label: "Pakkeshop", icon: Store },
  { k: "drift", label: "Roskilde pilot", icon: Shield },
];

export default function DistributionPlanningPanel() {
  const [mainTab, setMainTab] = React.useState("hubs");
  const [hubs, setHubs] = React.useState([]);
  const [regions, setRegions] = React.useState([]);
  const [runs, setRuns] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [regionFilter, setRegionFilter] = React.useState("");
  const [planHubId, setPlanHubId] = React.useState("roskilde");
  const [product, setProduct] = React.useState("same_day_evening");
  const [mode, setMode] = React.useState("full");
  const [planBusy, setPlanBusy] = React.useState(false);
  const [lastPlan, setLastPlan] = React.useState(null);
  const [postal, setPostal] = React.useState("");
  const [postalResult, setPostalResult] = React.useState(null);
  const [postalBusy, setPostalBusy] = React.useState(false);
  const [settlementType, setSettlementType] = React.useState("subcontractor");
  const [subcontractors, setSubcontractors] = React.useState([]);
  const [subcontractorId, setSubcontractorId] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [hubsRes, regionsRes, runsRes] = await Promise.all([
        adminApi().get("/admin/distribution/hubs"),
        adminApi().get("/admin/distribution/regions"),
        adminApi().get("/admin/planning/runs", { params: { limit: 15 } }),
      ]);
      const hubItems = hubsRes.data.items || [];
      setHubs(hubItems);
      setRegions(regionsRes.data.items || []);
      setRuns(runsRes.data.items || []);
      const active = hubItems.find((h) => h.active);
      if (active && !hubItems.some((h) => h.id === planHubId && h.active)) {
        setPlanHubId(active.id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente distribution-data");
    } finally {
      setLoading(false);
    }
  }, [planHubId]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    adminApi()
      .get("/admin/distribution/subcontractors/ready")
      .then(({ data }) => {
        const items = (data.items || []).filter((s) => s.ready_for_routes);
        setSubcontractors(items);
        if (items.length) {
          setSubcontractorId((prev) => prev || items[0].id);
        }
      })
      .catch(() => setSubcontractors([]));
  }, []);

  const activeHubs = hubs.filter((h) => h.active);
  const filteredHubs = regionFilter
    ? hubs.filter((h) => h.region_id === regionFilter)
    : hubs;

  const toggleActive = async (hub) => {
    const next = !hub.active;
    const msg = next
      ? `Aktivér ${hub.name}? Planlægning og retur-routing kan bruge denne HUB.`
      : `Deaktivér ${hub.name}? Ingen planlægning for denne HUB indtil den aktiveres igen.`;
    if (!window.confirm(msg)) return;
    try {
      await adminApi().patch(`/admin/distribution/hubs/${hub.id}`, { active: next });
      toast.success(next ? `${hub.name} er aktiv` : `${hub.name} er inaktiv`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke opdatere HUB");
    }
  };

  const runPlanning = async () => {
    setPlanBusy(true);
    setLastPlan(null);
    try {
      const { data } = await adminApi().post("/admin/planning/run", {
        hub_id: planHubId,
        product,
        mode,
        settlement_type: settlementType,
        subcontractor_id:
          settlementType === "subcontractor" ? subcontractorId || undefined : undefined,
        persist: true,
      });
      setLastPlan(data);
      if ((data.route_count || 0) === 0) toast.info(data.message || "Ingen pakker at planlægge");
      else toast.success(data.message || `${data.route_count} rute(r) genereret`);
      const runsRes = await adminApi().get("/admin/planning/runs", {
        params: { hub_id: planHubId, limit: 15 },
      });
      setRuns(runsRes.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Planlægning fejlede");
    } finally {
      setPlanBusy(false);
    }
  };

  const lookupPostal = async (e) => {
    e.preventDefault();
    const pc = postal.trim();
    if (!pc) return;
    setPostalBusy(true);
    setPostalResult(null);
    try {
      const { data } = await adminApi().get(`/admin/distribution/lookup/postal/${encodeURIComponent(pc)}`);
      setPostalResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Opslag fejlede");
    } finally {
      setPostalBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="text-brand-text2" data-testid="distribution-loading">
        <Loader2 className="animate-spin inline mr-2" size={16} />
        Indlæser distribution…
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="distribution-planning-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display font-bold text-2xl text-brand-dark flex items-center gap-2">
            <Warehouse size={24} />
            Distribution & planlægning
          </h2>
          <p className="text-sm text-brand-text2 mt-1">
            {hubs.length} HUBs i software · {activeHubs.length} aktive · hele Danmark dækket
          </p>
        </div>
        <button type="button" onClick={load} className="btn-secondary" data-testid="distribution-refresh">
          <RefreshCw size={16} /> Opdatér
        </button>
      </div>

      <div className="flex gap-1 border-b border-brand">
        {MAIN_TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.k}
              type="button"
              onClick={() => setMainTab(t.k)}
              data-testid={`distribution-tab-${t.k}`}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-2 ${
                mainTab === t.k ? "border-brand-red text-brand-red" : "border-transparent text-brand-text2"
              }`}
            >
              <Icon size={16} />
              {t.label}
            </button>
          );
        })}
      </div>

      {mainTab === "linehaul" ? (
        <DistributionLinehaulPanel />
      ) : mainTab === "vognmand" ? (
        <DistributionSubcontractorPanel />
      ) : mainTab === "pakkeshop" ? (
        <DistributionPakkeshopPanel />
      ) : mainTab === "drift" ? (
        <DistributionDriftPanel />
      ) : (
      <>
      {/* HUB network */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="font-semibold text-brand-dark">HUB-netværk</h3>
          <select
            className="input-flat py-1.5 text-sm"
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            data-testid="distribution-region-filter"
          >
            <option value="">Alle regioner</option>
            {regions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filteredHubs.map((hub) => (
            <div
              key={hub.id}
              className={`bg-white border p-4 ${hub.active ? "border-emerald-400" : "border-brand"}`}
              data-testid={`distribution-hub-${hub.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-brand-dark">{hub.name}</div>
                  <div className="text-xs text-brand-text2 mt-0.5">
                    {REGION_LABELS[hub.region_id] || hub.region_id} · {hub.postal_code} {hub.city}
                  </div>
                  {hub.pilot && (
                    <span className="inline-block mt-1 text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5">
                      Pilot
                    </span>
                  )}
                  {hub.linehaul_from_hub_id && (
                    <span className="inline-block mt-1 ml-1 text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5">
                      Linehaul fra {hub.linehaul_from_hub_id}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => toggleActive(hub)}
                  className={`text-xs px-2 py-1 border flex items-center gap-1 ${
                    hub.active
                      ? "border-emerald-500 text-emerald-700 bg-emerald-50"
                      : "border-gray-300 text-gray-600"
                  }`}
                  data-testid={`distribution-hub-toggle-${hub.id}`}
                >
                  {hub.active ? <CheckCircle2 size={14} /> : <Circle size={14} />}
                  {hub.active ? "Aktiv" : "Inaktiv"}
                </button>
              </div>
              {hub.notes && (
                <p className="text-xs text-brand-text2 mt-2 border-t border-brand pt-2">{hub.notes}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Run planning */}
        <section className="bg-white border border-brand p-5 space-y-4">
          <h3 className="font-semibold text-brand-dark flex items-center gap-2">
            <Play size={18} />
            Kør plan
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="label-flat">HUB</label>
              <select
                className="input-flat"
                value={planHubId}
                onChange={(e) => setPlanHubId(e.target.value)}
                data-testid="distribution-plan-hub"
              >
                {hubs.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                    {!h.active ? " (inaktiv)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-flat">Produkt</label>
              <select
                className="input-flat"
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                data-testid="distribution-plan-product"
              >
                {PRODUCT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-flat">Type</label>
              <select
                className="input-flat"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                data-testid="distribution-plan-mode"
              >
                {MODE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-flat">Afregning</label>
              <select
                className="input-flat"
                value={settlementType}
                onChange={(e) => setSettlementType(e.target.value)}
                data-testid="distribution-plan-settlement"
              >
                {SETTLEMENT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            {settlementType === "subcontractor" && (
              <div className="sm:col-span-2">
                <label className="label-flat">Vognmand</label>
                <select
                  className="input-flat"
                  value={subcontractorId}
                  onChange={(e) => setSubcontractorId(e.target.value)}
                  data-testid="distribution-plan-subcontractor"
                >
                  <option value="">Vælg vognmand</option>
                  {subcontractors.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.company_name}
                    </option>
                  ))}
                </select>
                {subcontractors.length === 0 && (
                  <p className="text-xs text-amber-700 mt-1">
                    Ingen klar vognmand — godkend onboarding under Admin → Vognmænd.
                  </p>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={runPlanning}
            disabled={planBusy}
            className="btn-primary"
            data-testid="distribution-plan-run"
          >
            {planBusy ? <Loader2 className="animate-spin" size={16} /> : <Play size={16} />}
            Kør planlægning
          </button>
          {lastPlan && (
            <div className="text-sm border-t border-brand pt-3 space-y-2" data-testid="distribution-plan-result">
              <p className="font-medium">{lastPlan.message}</p>
              <p className="text-brand-text2">
                Run: <code className="text-xs">{lastPlan.planning_run_id}</code>
              </p>
              {(lastPlan.routes || []).map((route) => (
                <div key={route.id} className="bg-brand-bg2 p-3 text-xs">
                  <div className="font-semibold">{route.route_number}</div>
                  <div>
                    {route.route_type === "pickup" ? "Pickup" : "Levering"}
                    {route.zone_id ? ` · zone ${route.zone_id}` : ""}
                    {" · "}
                    {(route.parcel_ids || []).length} pakke(r)
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Postal lookup */}
        <section className="bg-white border border-brand p-5 space-y-4">
          <h3 className="font-semibold text-brand-dark flex items-center gap-2">
            <MapPin size={18} />
            Postnr → HUB
          </h3>
          <form onSubmit={lookupPostal} className="flex gap-2">
            <input
              className="input-flat flex-1"
              placeholder="fx 4000 eller 3700"
              value={postal}
              onChange={(e) => setPostal(e.target.value)}
              data-testid="distribution-postal-input"
            />
            <button type="submit" className="btn-secondary" disabled={postalBusy} data-testid="distribution-postal-lookup">
              {postalBusy ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
            </button>
          </form>
          {postalResult && (
            <div className="text-sm bg-brand-bg2 p-3" data-testid="distribution-postal-result">
              {postalResult.covered ? (
                <>
                  <span className="font-semibold">{postalResult.postal_code}</span>
                  {" → "}
                  <span className="text-brand-red">{postalResult.hub?.name || postalResult.hub_id}</span>
                  {!postalResult.hub?.active && (
                    <span className="text-amber-700"> (HUB inaktiv i software)</span>
                  )}
                </>
              ) : (
                <span className="text-red-700">Postnummer ikke dækket</span>
              )}
            </div>
          )}

          <div className="border-t border-brand pt-4">
            <h4 className="text-sm font-semibold text-brand-dark mb-2">Seneste planlægninger</h4>
            {runs.length === 0 ? (
              <p className="text-xs text-brand-text2">Ingen runs endnu</p>
            ) : (
              <ul className="space-y-2 max-h-48 overflow-y-auto">
                {runs.map((run) => (
                  <li key={run.id} className="text-xs border-b border-brand pb-2">
                    <span className="font-medium">{run.hub_name || run.hub_id}</span>
                    {" · "}
                    {run.product} · {run.route_count} rute(r)
                    <br />
                    <span className="text-brand-text2">{run.created_at_iso?.slice(0, 19)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
      </>
      )}
    </div>
  );
}
